import { useModal } from "@/Contexts";
import { useGlobalAlert } from "@/States/globalAlert";
import { EMAIL_REGEX } from "@/Utils/isValidEmail";
import { isValidPhoneNumberAllowEmpty } from "@/Utils/isValidPhoneNumber";
import { addCountryCodeToPhone } from "@/Utils/phoneCountryCode";
import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import { FormFieldType, GridForm } from "@/components/GridForm";
import ModalContainer from "@/components/modalContainer";
import type { Person } from "@/pages/customer/components/CustomerDrawer";
import { useTrans } from "@/translations";

import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";

import { isNil, mapValues } from "lodash";

import type { ReportJob } from "./PeopleCard";

interface PeopleModalProps {
  customerId: string;
  people: Person[];
  onConfirm: () => void;
  reportJobs: ReportJob[];
}

type PeopleForm = {
  people: Person[];
};

export const PeopleModal: FC<PeopleModalProps> = ({
  customerId,
  people,
  onConfirm,
  reportJobs,
}) => {
  const { isMWM } = useConfig();
  const { t } = useTrans();
  const { hideModal } = useModal();
  const { setGlobalAlert } = useGlobalAlert();

  const formHandler = useForm<PeopleForm>({
    mode: "onTouched",
    defaultValues: {
      people: people.length
        ? people
            .map((person) =>
              mapValues(person, (value) => (isNil(value) ? "" : value)),
            )
            .map((person) => ({
              ...person,
              phone: addCountryCodeToPhone(person.phone ?? ""),
            }))
        : [{}],
    },
  });

  useEffect(() => {
    if (people.length > 0) {
      formHandler.trigger();
    }
  }, [people, formHandler]);

  const {
    handleSubmit,
    formState: { isDirty, isValid },
  } = formHandler;

  const {
    mutate,
    isPending: isLoading,
    isSuccess,
  } = trpc.customers.update.useMutation();

  useEffect(() => {
    if (isSuccess) {
      setGlobalAlert({
        type: "success",
        message: "systemMessages:changesSaved",
      });
      onConfirm();
      hideModal();
    }
  }, [isSuccess, hideModal, onConfirm, setGlobalAlert]);

  const { mutate: deleteEmailJob } = trpc.config.deleteEmailJob.useMutation();

  const onSubmit = handleSubmit((formData) => {
    const people = formData.people;
    const mainContact = people[0] as Person | undefined;

    const otherContacts = people.slice(1);

    const contactPeople = JSON.stringify(otherContacts) ?? undefined;

    const customer = {
      id: customerId,
      contactEmail: mainContact?.email ?? null,
      contactMobile: mainContact?.phone ?? null,
      contactPerson: mainContact?.name ?? null,
      contactPeople,
    };

    mutate({
      customer,
    });

    const updatedCustomerEmails = people.map((p) => p.email);
    const emailJobsToDelete = reportJobs.filter(
      (j) => !updatedCustomerEmails.includes(j.email),
    );

    if (emailJobsToDelete.length) {
      for (const job of emailJobsToDelete) {
        deleteEmailJob({ emailJobId: job.id.toString() });
      }
    }
  });

  // Disable form inputs and button when loading or after saving
  const columns = [
    {
      label: t("formLabels:name"),
      name: "name",
      enabled: true,
      required: !isMWM,
    },
    {
      label: t("formLabels:email"),
      name: "email",
      enabled: true,
      regexPattern: EMAIL_REGEX,
      required: true,
      patternError: "errors:mustBeValidEmail",
    },
    {
      label: t("formLabels:phoneNumber"),
      name: "phone",
      enabled: true,
      validationFunction: isValidPhoneNumberAllowEmpty,
      patternError: "errors:mustBeValidPhoneNumber",
      fieldType: FormFieldType.PHONE,
    },
  ];

  return (
    <ModalContainer
      title={"drawerLabels:contactPersons"}
      onCancel={hideModal}
      onConfirm={() => onSubmit()}
      onConfirmLoading={isLoading}
      showDiscardModal={isDirty}
      onConfirmDisabled={!isValid}
    >
      <div className="mb-5 min-w-[700px]">
        <GridForm<Person[]>
          enabled={!isLoading}
          formHandler={formHandler}
          columns={columns}
          enableDeleteColumn
          enableAddRowButton
        />
      </div>
    </ModalContainer>
  );
};
